import React, { useState, useMemo, useCallback } from 'react';
import {
  MdSchedule, MdInventory2, MdMedication, MdWarningAmber, MdEventBusy, MdAdd, MdDelete,
  MdNotificationsActive, MdCheckCircle, MdClose, MdEdit, MdExpandMore, MdExpandLess, MdVisibility,
} from 'react-icons/md';
import { PageShell, Badge, DataTable, StatCard } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { printPharmacyBill } from '../../utils/hospitalPdf';
import { dosingLabel } from '../../utils/emrHelpers';
import { runSafetyCheck, suggestFefoBatch, suggestSubstitutes } from '../../utils/pharmacySafety';

function statusTone(s) {
  const n = normStatus(s);
  if (n === 'dispensed') return 'success';
  if (n === 'dispensing') return 'info';
  if (n === 'cancelled') return 'danger';
  return 'warning';
}

// Normalize order status for filtering — backend/legacy rows mix casing ('Due'/'due'/'pending')
function normStatus(s) {
  return String(s || '').toLowerCase().trim();
}
function isPendingStatus(s) {
  return ['pending', 'dispensing', 'due'].includes(normStatus(s));
}

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Expiry stored as "YYYY-MM". Returns 'expired' | 'soon' | 'ok' | ''
function expiryState(expiry) {
  if (!expiry) return '';
  const [y, m] = String(expiry).split('-').map(Number);
  if (!y || !m) return '';
  const end = new Date(y, m, 0);
  const now = new Date();
  const days = (end - now) / 86400000;
  if (days < 0) return 'expired';
  if (days <= 90) return 'soon';
  return 'ok';
}

function lineTotal(l) {
  // MRP is GST-inclusive (India) — do not add GST on top
  const gross = Number(l.qty || 0) * Number(l.mrp || 0);
  return gross - (gross * Number(l.discPct || 0)) / 100;
}

const EMPTY_MED = {
  name: '', brandName: '', genericName: '', saltComposition: '', therapeuticClass: '',
  manufacturer: '', form: 'Tablet', unit: 'nos', packSize: '', barcode: '',
  batch: '', expiry: '', mrp: '', purchasePrice: '', gstPct: 12, stockQty: '', reorderLevel: 10,
  hsn: '', rackNo: '', imageUrl: '', isOtc: false,
};

function matchInventoryLines(lines, inventory) {
  return (lines || []).map((l) => {
    const needle = (l.name || l.drug || '').toLowerCase().trim();
    let hit = l.itemId ? inventory.find((i) => i.itemId === l.itemId) : null;
    if (!hit && needle) {
      // Prefer exact name / brand / salt — avoid loose substring matches that hit the wrong drug
      hit = inventory.find((i) => (i.name || '').toLowerCase() === needle)
        || inventory.find((i) => (i.brandName || '').toLowerCase() === needle)
        || inventory.find((i) => {
          const g = (i.saltComposition || i.genericName || '').toLowerCase();
          return g && g === needle;
        });
      if (!hit && needle.length >= 5) {
        hit = inventory.find((i) => {
          const n = (i.name || '').toLowerCase();
          const brand = (i.brandName || '').toLowerCase();
          return n.startsWith(needle) || needle.startsWith(n) || brand === needle;
        });
      }
    }
    if (!hit) return l;
    // FEFO: prefer earliest-expiry stock of the same drug
    const fefo = suggestFefoBatch(hit, inventory) || hit;
    return {
      ...l,
      itemId: fefo.itemId,
      name: fefo.name,
      mrp: Number(fefo.mrp || l.mrp || 0),
      gstPct: Number(fefo.gstPct ?? l.gstPct ?? 0),
      discPct: Number(l.discPct || 0),
      batch: fefo.batch || l.batch,
      expiry: fefo.expiry || l.expiry,
      hsn: fefo.hsn || l.hsn,
      fefo: fefo.itemId !== hit.itemId || (fefo.expiry && fefo.expiry !== hit.expiry),
    };
  });
}

function orderAmount(order) {
  if (order.amount != null) return Number(order.amount);
  const lines = order.lines || [];
  return lines.reduce((s, l) => s + lineTotal(l), 0);
}

function sourceLabel(source) {
  const map = {
    emr: 'Doctor EMR',
    doctor_queue: 'Doctor queue',
    counter: 'Counter sale',
    reception: 'Reception',
    ipd: 'IPD / Ward',
  };
  return map[source] || source || 'Prescription';
}

function orderLines(order) {
  return order.lines?.length ? order.lines : (order.rxLines || []).map((r) => ({
    name: r.drug || r.name,
    qty: r.qty,
    mrp: r.mrp,
    itemId: r.itemId,
  }));
}

function orderMedPreview(order) {
  const lines = orderLines(order);
  if (!lines.length) return order.items || 'No medicines listed';
  const names = lines.map((l) => `${l.name || l.drug}${l.qty ? ` ×${l.qty}` : ''}`);
  return names;
}

export default function PharmacyDesk() {
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { data, loading, error: ordersError, reload } = useLiveData(() => ipApi.getPharmacyOrders(80), []);
  const { data: invData, loading: invLoading, reload: reloadInv } = useLiveData(() => ipApi.getPharmacyInventory('', { includeInactive: 1 }), []);
  const orders = data?.orders || [];
  const inventory = useMemo(() => invData?.items || [], [invData]);
  const activeInventory = useMemo(() => inventory.filter((i) => i.active !== false), [inventory]);

  const [tab, setTab] = useState('orders');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [inventoryAutoAdd, setInventoryAutoAdd] = useState(false);

  const pending = orders.filter((r) => isPendingStatus(r.status));
  const done = orders.filter((r) => normStatus(r.status) === 'dispensed');
  const lowStock = activeInventory.filter((i) => Number(i.stockQty) <= Number(i.reorderLevel || 0));
  const expiring = activeInventory.filter((i) => ['soon', 'expired'].includes(expiryState(i.expiry)));
  const stockValue = activeInventory.reduce((s, i) => s + Number(i.stockQty || 0) * Number(i.purchasePrice || i.mrp || 0), 0);

  const setStatus = async (order, status, patch = {}) => {
    setBusy(order.orderId);
    setMsg('');
    try {
      let body = { ...patch };
      if (status === 'dispensed') {
        let lines = matchInventoryLines(patch.lines || order.lines || [], inventory);
        // Clinical safety (allergies + interactions) — overrideable like Epic MedAlerts
        const drugNames = lines.map((l) => l.name).filter(Boolean);
        if (drugNames.length) {
          const safety = runSafetyCheck({
            allergies: order.allergies || order.patientAllergies || '',
            drugs: drugNames,
          });
          if (safety.blocking) {
            const detail = [
              ...safety.allergies.map((a) => `• ${a.note}`),
              ...safety.interactions.map((i) => `• ${i.drugA} + ${i.drugB}: ${i.note}`),
            ].join('\n');
            if (!window.confirm(`⚠ SAFETY ALERT before dispense\n\n${detail}\n\nDispense anyway?`)) {
              throw new Error(`Dispense blocked: ${safety.summary}`);
            }
          }
        }
        // Partial dispense: reduce qty to available stock when pharmacist confirms
        const shortfalls = [];
        lines = lines.map((l) => {
          if (!l.itemId) return l;
          const inv = inventory.find((i) => i.itemId === l.itemId);
          if (!inv) return l;
          if (expiryState(inv.expiry) === 'expired') {
            shortfalls.push({ type: 'expired', name: l.name });
            return l;
          }
          const need = Number(l.qty || 0);
          const have = Number(inv.stockQty || 0);
          if (need > have) {
            const subs = suggestSubstitutes(l.name, inventory);
            shortfalls.push({ type: 'short', name: l.name, need, have, subs });
            return { ...l, qty: have, originalQty: need, partial: true };
          }
          return l;
        });
        if (shortfalls.some((s) => s.type === 'expired')) {
          throw new Error(`Cannot dispense expired stock: ${shortfalls.filter((s) => s.type === 'expired').map((s) => s.name).join(', ')}`);
        }
        const shorts = shortfalls.filter((s) => s.type === 'short');
        if (shorts.length) {
          const msgLines = shorts.map((s) => {
            const subHint = s.subs?.length ? ` — try: ${s.subs.map((x) => x.name).join(', ')}` : '';
            return `• ${s.name}: need ${s.need}, have ${s.have}${subHint}`;
          }).join('\n');
          if (!window.confirm(`Partial stock available:\n\n${msgLines}\n\nDispense available qty only?`)) {
            throw new Error('Dispense cancelled — insufficient stock');
          }
          lines = lines.filter((l) => Number(l.qty) > 0);
        }
        const amt = lines.reduce((s, l) => s + lineTotal(l), 0);
        if (!lines.length) throw new Error('Cannot dispense: no medicines on this order.');
        if (amt <= 0) throw new Error('Cannot dispense: amount is zero. Click "Match stock" or use Counter Sale.');
        const unmatched = lines.filter((l) => Number(l.qty) > 0 && Number(l.mrp || 0) > 0 && !l.itemId);
        if (unmatched.length) {
          throw new Error(`Cannot dispense — not linked to stock: ${unmatched.map((l) => l.name || 'item').join(', ')}. Click "Match stock" first.`);
        }
        body = { ...body, status, lines, amount: amt, partialDispense: shorts.length > 0 };
      } else {
        body = { ...body, status };
      }
      await ipApi.patchPharmacyOrder(order.orderId, body);
      await Promise.all([reload(), reloadInv()]);
      if (status === 'dispensed' && body.partialDispense) {
        setMsg('Partially dispensed — remaining qty still due. Restock or substitute.');
      }
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const matchAndSave = async (order) => {
    const lines = matchInventoryLines(order.lines || [], inventory);
    const amount = lines.reduce((s, l) => s + lineTotal(l), 0);
    setBusy(order.orderId);
    setMsg('');
    try {
      await ipApi.patchPharmacyOrder(order.orderId, { lines, amount });
      setMsg('Prices matched from inventory. You can now dispense.');
      await reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const saveOrderLines = async (order, lines) => {
    const matched = matchInventoryLines(lines, inventory);
    const amount = matched.reduce((s, l) => s + lineTotal(l), 0);
    setBusy(order.orderId);
    setMsg('');
    try {
      await ipApi.patchPharmacyOrder(order.orderId, { lines: matched, amount });
      setMsg('Order medicines updated.');
      await reload();
    } catch (e) {
      setMsg(e.message);
      throw e;
    } finally {
      setBusy('');
    }
  };

  return (
    <PageShell
      title="Pharmacy"
      subtitle="Inventory · Counter Sales · EMR Prescriptions"
      actions={
        <div className="row-actions">
          <button type="button" className="btn btn-primary" onClick={() => { setTab('inventory'); setInventoryAutoAdd(true); }} style={{ marginRight: 8 }}>
            <MdAdd style={{ verticalAlign: '-2px', marginRight: 4 }} /> Add Medicine
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { reload(); reloadInv(); }}>Refresh</button>
        </div>
      }
    >
      {/* Getting Started Banner — shown when no medicines in inventory */}
      {!invLoading && inventory.length === 0 && (
        <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0d6e6a 100%)', borderRadius: 16, padding: '24px 28px', marginBottom: 20, color: '#fff' }}>
          <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 6 }}>👋 Welcome to Pharmacy!</div>
          <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 20 }}>Your pharmacy is empty. Follow these 3 steps to get started:</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            {[
              { step: '1', icon: '💊', title: 'Add Medicines to Inventory', desc: 'Go to the Inventory tab → Click "Add Medicine" → Enter name, batch, expiry, MRP, stock quantity, GST%', action: 'Go to Inventory →', tab: 'inventory', color: '#3b82f6' },
              { step: '2', icon: '🛒', title: 'Make a Counter Sale', desc: 'Go to New Sale tab → Search patient → Select medicine from inventory → Set quantity → Save & Print Bill', action: 'New Sale →', tab: 'sale', color: '#10b981' },
              { step: '3', icon: '🖨', title: 'Print GST Bill / Dispensed', desc: 'After sale, bill auto-prints. You can also re-print from the Orders tab for any dispensed order.', action: 'View Orders →', tab: 'orders', color: '#f59e0b' },
            ].map(({ step, icon, title, desc, action, tab: t, color }) => (
              <div key={step} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: 16, backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ background: color, color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 13, flexShrink: 0 }}>{step}</span>
                  <span style={{ fontSize: 22 }}>{icon}</span>
                  <span style={{ fontWeight: 800, fontSize: 14 }}>{title}</span>
                </div>
                <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.5, marginBottom: 10 }}>{desc}</div>
                <button type="button" onClick={() => setTab(t)} style={{ background: color, border: 'none', color: '#fff', borderRadius: 8, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{action}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="stat-grid">
        <StatCard label="Pending orders" value={pending.length} accent="#f59e0b" icon={MdSchedule} loading={loading} />
        <StatCard label="Dispensed" value={done.length} accent="#10b981" icon={MdInventory2} loading={loading} />
        <StatCard label="Medicines in Stock" value={inventory.length} accent="#4f8ef7" icon={MdMedication} loading={invLoading} onClick={() => setTab('inventory')} style={{ cursor: 'pointer' }} />
        <StatCard label="Low stock" value={lowStock.length} accent="#ef4444" icon={MdWarningAmber} loading={invLoading} />
        <StatCard label="Expiring / expired" value={expiring.length} accent="#f97316" icon={MdEventBusy} loading={invLoading} />
        <StatCard label="Stock value" value={inr(stockValue)} accent="#8b5cf6" icon={MdInventory2} loading={invLoading} />
      </div>

      {msg && <div className={`mb ${/fail|error|not/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}<button type="button" style={{ marginLeft: 10, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setMsg('')}>✕</button></div>}

      {ordersError && (
        <div className="error mb" role="alert">
          Could not load pharmacy orders: {ordersError.message || String(ordersError)}
          <button type="button" className="btn btn-sm btn-secondary" style={{ marginLeft: 10 }} onClick={reload}>Retry</button>
        </div>
      )}

      <div className="period-tabs card mb" style={{ alignItems: 'center' }}>
        <button type="button" className={`emr-chip ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>📋 Orders ({pending.length})</button>
        <button type="button" className={`emr-chip ${tab === 'sale' ? 'active' : ''}`} onClick={() => setTab('sale')}
          style={{ background: tab === 'sale' ? '#10b981' : '', color: tab === 'sale' ? '#fff' : '#10b981', fontWeight: 800, border: '2px solid #10b981' }}>
          🛒 New Sale
        </button>
        <button type="button" className={`emr-chip ${tab === 'inventory' ? 'active' : ''}`} onClick={() => setTab('inventory')}
          style={{ position: 'relative', fontWeight: inventory.length === 0 ? 800 : 600, border: inventory.length === 0 ? '2px solid #3b82f6' : '' }}>
          💊 Inventory ({inventory.length})
          {inventory.length === 0 && <span style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>!</span>}
        </button>
        <button
          type="button"
          className={`emr-chip ${tab === 'alerts' ? 'active' : ''}`}
          onClick={() => setTab('alerts')}
          style={lowStock.length + expiring.length > 0 ? { color: '#dc2626', fontWeight: 800 } : {}}
        >
          <MdNotificationsActive style={{ verticalAlign: '-2px', marginRight: 4 }} />
          Alerts {lowStock.length + expiring.length > 0 ? `(${lowStock.length + expiring.length})` : ''}
        </button>
        <button type="button" className={`emr-chip ${tab === 'purchase' ? 'active' : ''}`} onClick={() => setTab('purchase')}>📦 Purchase Orders</button>
        <button type="button" className={`emr-chip ${tab === 'suppliers' ? 'active' : ''}`} onClick={() => setTab('suppliers')}>🏭 Suppliers</button>
        <button type="button" className={`emr-chip ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>📊 Reports</button>
        <button type="button" className={`emr-chip ${tab === 'returns' ? 'active' : ''}`} onClick={() => setTab('returns')}>↩ Drug Returns</button>
        <button type="button" className={`emr-chip ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>📈 Analytics</button>
      </div>

      {tab === 'orders' && (
        <OrdersTab
          orders={orders}
          inventory={inventory}
          loading={loading}
          busy={busy}
          onSetStatus={setStatus}
          onMatchStock={matchAndSave}
          onSaveLines={saveOrderLines}
          onPrint={(r) => printPharmacyBill(r, hospital, user)}
        />
      )}

      {tab === 'sale' && (
        <NewSale
          inventory={activeInventory}
          onDone={async (m) => { setMsg(m); setTab('orders'); await Promise.all([reload(), reloadInv()]); }}
          onError={setMsg}
          hospital={hospital}
          user={user}
          onGoToInventory={() => setTab('inventory')}
        />
      )}

      {tab === 'inventory' && (
        <InventoryTab
          inventory={inventory} loading={invLoading}
          autoOpenAdd={inventoryAutoAdd}
          onAutoOpenDone={() => setInventoryAutoAdd(false)}
          onChanged={async (m) => { if (m) setMsg(m); await reloadInv(); }}
          onError={setMsg}
        />
      )}

      {tab === 'alerts' && (
        <AlertsTab lowStock={lowStock} expiring={expiring} />
      )}

      {tab === 'purchase' && <PurchaseOrdersTab onError={setMsg} onStockChange={reloadInv} />}
      {tab === 'suppliers' && <SuppliersTab onError={setMsg} />}
      {tab === 'reports' && <PharmacyReportsTab orders={orders} inventory={inventory} />}
      {tab === 'returns' && <DrugReturnsTab inventory={inventory} onError={setMsg} onStockChange={reloadInv} />}
      {tab === 'analytics' && <PharmacyAnalyticsTab inventory={inventory} orders={orders} />}
    </PageShell>
  );
}

/* ── Pharmacy Alerts Tab ── */
function AlertsTab({ lowStock, expiring }) {
  const all = lowStock.length === 0 && expiring.length === 0;
  return (
    <div>
      {all && (
        <div className="ph-alert-clear">
          <MdCheckCircle style={{ fontSize: 32, color: '#10b981' }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>All clear!</div>
            <div style={{ color: '#64748b', fontSize: 13 }}>No low-stock or expiring medicines right now.</div>
          </div>
        </div>
      )}
      {lowStock.length > 0 && (
        <div className="ph-alert-section">
          <div className="ph-alert-section-head" style={{ background: '#fef2f2', borderColor: '#fca5a5', color: '#dc2626' }}>
            <MdWarningAmber style={{ fontSize: 18 }} />
            Low Stock Alert — {lowStock.length} medicine{lowStock.length > 1 ? 's' : ''} below reorder level
          </div>
          <div className="ph-alert-grid">
            {lowStock.map((item) => (
              <div key={item.itemId || item.name} className="ph-alert-card" style={{ borderLeft: '4px solid #ef4444' }}>
                <div className="ph-alert-name">{item.name}</div>
                <div className="ph-alert-generic">{item.genericName || item.form || ''}</div>
                <div className="ph-alert-row">
                  <span className="ph-alert-chip" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>
                    Stock: {item.stockQty} {item.unit || 'units'}
                  </span>
                  <span className="ph-alert-chip" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
                    Reorder: {item.reorderLevel}
                  </span>
                </div>
                {item.rackNo && <div className="ph-alert-rack">Rack: {item.rackNo}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {expiring.length > 0 && (
        <div className="ph-alert-section">
          <div className="ph-alert-section-head" style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#ea580c' }}>
            <MdEventBusy style={{ fontSize: 18 }} />
            Expiry Alert — {expiring.length} medicine{expiring.length > 1 ? 's' : ''} expiring soon or expired
          </div>
          <div className="ph-alert-grid">
            {expiring.map((item) => {
              const state = (() => {
                if (!item.expiry) return '';
                const [y, m] = String(item.expiry).split('-').map(Number);
                if (!y || !m) return '';
                const end = new Date(y, m, 0);
                const now = new Date();
                const days = (end - now) / 86400000;
                if (days < 0) return 'expired';
                if (days <= 30) return 'critical';
                return 'soon';
              })();
              const colors = {
                expired: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', left: '#ef4444' },
                critical: { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', left: '#f97316' },
                soon: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a', left: '#eab308' },
              };
              const c = colors[state] || colors.soon;
              return (
                <div key={item.itemId || item.name} className="ph-alert-card" style={{ borderLeft: `4px solid ${c.left}` }}>
                  <div className="ph-alert-name">{item.name}</div>
                  <div className="ph-alert-generic">{item.batch ? `Batch: ${item.batch}` : ''}</div>
                  <div className="ph-alert-row">
                    <span className="ph-alert-chip" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
                      {state === 'expired' ? '❌ EXPIRED' : state === 'critical' ? '⚠ Expires soon' : '⏰ Check expiry'}: {item.expiry}
                    </span>
                    <span className="ph-alert-chip" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
                      Qty: {item.stockQty}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Order detail drawer (full Rx + edit + dispense) ── */
function OrderDetailDrawer({ order, inventory, busy, onClose, onSetStatus, onMatchStock, onSaveLines, onPrint }) {
  const [editLines, setEditLines] = useState(() => JSON.parse(JSON.stringify(order.lines || [])));
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  // Re-sync when the order changes underneath us (e.g. after "Match stock")
  React.useEffect(() => {
    if (!editMode) setEditLines(JSON.parse(JSON.stringify(order.lines || [])));
  }, [order.lines, editMode]);
  const rxLines = order.rxLines || [];
  const canEdit = !['dispensed', 'cancelled'].includes(order.status);
  const needsMatch = orderAmount(order) <= 0 || !(order.lines || []).some((l) => l.itemId);
  const amt = editMode ? editLines.reduce((s, l) => s + lineTotal(l), 0) : orderAmount(order);

  const updateLine = (idx, patch) => setEditLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const pickInventory = (idx, itemId) => {
    const it = inventory.find((i) => i.itemId === itemId);
    if (!it) return;
    updateLine(idx, {
      itemId: it.itemId, name: it.name, batch: it.batch || '',
      mrp: Number(it.mrp || 0), gstPct: Number(it.gstPct || 0),
      stockQty: Number(it.stockQty || 0),
    });
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      await onSaveLines(order, editLines);
      setEditMode(false);
    } catch { /* parent shows error */ }
    finally { setSaving(false); }
  };

  return (
    <div className="rcp-overlay" onClick={onClose} role="presentation">
      <div className="rcp-drawer ph-order-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Pharmacy order details">
        <div className="rcp-drawer-head">
          <div className="rcp-drawer-top">
            <div className="rcp-id">
              <div className="rcp-avatar" style={{ background: '#0d6e6a' }}>💊</div>
              <div>
                <div className="rcp-name">{order.patientName || 'Patient'}</div>
                <div className="rcp-sub">{order.orderId} · {sourceLabel(order.source)} · <Badge tone={statusTone(order.status)}>{order.status}</Badge></div>
              </div>
            </div>
            <button type="button" className="rcp-close" onClick={onClose} aria-label="Close"><MdClose /></button>
          </div>
          <div className="rcp-actions">
            {canEdit && needsMatch && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy === order.orderId} onClick={() => onMatchStock(order)}>Match stock</button>
            )}
            {canEdit && !editMode && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}><MdEdit style={{ verticalAlign: '-2px' }} /> Edit medicines</button>
            )}
            {editMode && (
              <>
                <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={saveEdits}>{saving ? 'Saving…' : 'Save changes'}</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditLines(JSON.parse(JSON.stringify(order.lines || []))); setEditMode(false); }}>Cancel edit</button>
              </>
            )}
            {canEdit && order.status !== 'dispensing' && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy === order.orderId} onClick={() => onSetStatus(order, 'dispensing')}>Start dispensing</button>
            )}
            {canEdit && (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy === order.orderId} onClick={() => onSetStatus(order, 'dispensed')}>Dispense &amp; bill</button>
            )}
            {order.status === 'dispensed' && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => onPrint(order)}>Print bill</button>
            )}
            {canEdit && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy === order.orderId} onClick={() => { if (window.confirm('Cancel this pharmacy order?')) onSetStatus(order, 'cancelled'); }}>Cancel order</button>
            )}
          </div>
        </div>
        <div className="rcp-body">
          <dl className="rcp-kv mb">
            <dt>Doctor</dt><dd>{order.doctorName || '—'}</dd>
            <dt>Payment</dt><dd>{order.paymentMode || 'Cash'}</dd>
            <dt>Admission / IP</dt><dd>{order.admissionId || '—'}</dd>
            <dt>Created</dt><dd>{(order.createdAt || '').slice(0, 16).replace('T', ' ')}</dd>
            <dt>Created by</dt><dd>{order.createdByName || order.createdBy || '—'}</dd>
            <dt>Amount</dt><dd style={{ fontWeight: 800, color: '#0d6e6a' }}>{inr(amt)}</dd>
            {order.recordId && <><dt>EMR record</dt><dd>{order.recordId}</dd></>}
            {order.notes && <><dt>Notes</dt><dd>{order.notes}</dd></>}
          </dl>

          {rxLines.length > 0 && (
            <section className="mb">
              <h4 style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b', textTransform: 'uppercase' }}>Doctor prescription (Rx)</h4>
              <div className="ph-rx-list">
                {rxLines.map((r, i) => (
                  <div key={i} className="ph-rx-line">
                    <div className="ph-rx-drug">{i + 1}. {r.drug}{r.generic ? <span className="hint"> ({r.generic})</span> : ''}</div>
                    <div className="ph-rx-meta">
                      {[r.dose, dosingLabel(r), r.food, r.duration, r.route && r.route !== 'Oral' ? r.route : ''].filter(Boolean).join(' · ') || '—'}
                      {r.instructions && <span> · {r.instructions}</span>}
                    </div>
                    <div className="ph-rx-qty">Dispense qty: <strong>{r.qty || '—'}</strong></div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h4 style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b', textTransform: 'uppercase' }}>
              Billable medicines ({(editMode ? editLines : order.lines || []).length})
            </h4>
            {(editMode ? editLines : order.lines || []).length === 0 ? (
              <p className="hint">No bill lines yet — click Match stock or Edit to link inventory.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Medicine</th><th>Qty</th><th>MRP</th><th>Disc%</th><th>GST%</th><th>Stock</th><th>Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editMode ? editLines : order.lines || []).map((l, idx) => {
                      const inv = inventory.find((i) => i.itemId === l.itemId);
                      const lowStock = inv && Number(l.qty) > Number(inv.stockQty);
                      const noMatch = !l.itemId;
                      return (
                        <tr key={idx} style={noMatch ? { background: '#fffbeb' } : undefined}>
                          <td>
                            {editMode ? (
                              <select className="input input-sm" value={l.itemId || ''} onChange={(e) => pickInventory(idx, e.target.value)}>
                                <option value="">— Select from inventory —</option>
                                {inventory.map((i) => (
                                  <option key={i.itemId} value={i.itemId}>{i.name} (stock {i.stockQty})</option>
                                ))}
                              </select>
                            ) : (
                              <>
                                <strong>{l.name}</strong>
                                {l.batch && <div className="hint">Batch {l.batch}</div>}
                                {noMatch && <div className="hint" style={{ color: '#ca8a04' }}>Not linked to inventory</div>}
                              </>
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input className="input input-sm" type="number" min={1} style={{ width: 64 }} value={l.qty} onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })} />
                            ) : l.qty}
                          </td>
                          <td>{editMode ? <input className="input input-sm" type="number" min={0} style={{ width: 80 }} value={l.mrp} onChange={(e) => updateLine(idx, { mrp: Number(e.target.value) })} /> : inr(l.mrp)}</td>
                          <td>{editMode ? <input className="input input-sm" type="number" min={0} max={100} style={{ width: 56 }} value={l.discPct || 0} onChange={(e) => updateLine(idx, { discPct: Number(e.target.value) })} /> : `${l.discPct || 0}%`}</td>
                          <td>{l.gstPct || 0}%</td>
                          <td style={lowStock ? { color: '#dc2626', fontWeight: 700 } : undefined}>
                            {inv ? `${inv.stockQty} ${inv.unit || ''}` : '—'}
                            {lowStock && <div className="hint">Low!</div>}
                          </td>
                          <td style={{ fontWeight: 700 }}>{inr(lineTotal(l))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {order.prescription && !rxLines.length && (
            <section className="mb" style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 13, color: '#64748b' }}>Prescription text</h4>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#f8fafc', padding: 12, borderRadius: 8 }}>{order.prescription}</pre>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Orders / dispensing — expandable cards + detail drawer ── */
function OrdersTab({ orders, inventory, loading, busy, onSetStatus, onMatchStock, onSaveLines, onPrint }) {
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const pending = orders.filter((r) => isPendingStatus(r.status));
  const done = orders.filter((r) => normStatus(r.status) === 'dispensed');
  const rows = filter === 'pending' ? pending : filter === 'done' ? done : orders;

  const openDetail = (order) => {
    setDetailOrder(order);
    setExpandedId(order.orderId);
  };

  return (
    <>
      <div className="staff-filter-tabs mb">
        <button type="button" className={`tab-chip ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>Pending ({pending.length})</button>
        <button type="button" className={`tab-chip ${filter === 'done' ? 'active' : ''}`} onClick={() => setFilter('done')}>Dispensed ({done.length})</button>
        <button type="button" className={`tab-chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All ({orders.length})</button>
      </div>

      {loading && <p className="hint">Loading pharmacy orders…</p>}

      {!loading && rows.length === 0 && (
        <p className="hint table-empty">No orders here. EMR prescriptions and counter sales appear in this list.</p>
      )}

      <div className="ph-order-list">
        {rows.map((order) => {
          const preview = orderMedPreview(order);
          const previewArr = Array.isArray(preview) ? preview : [preview];
          const expanded = expandedId === order.orderId;
          const lines = order.lines || [];
          const needsMatch = orderAmount(order) <= 0 || !lines.some((l) => l.itemId);
          const isPending = !['dispensed', 'cancelled'].includes(order.status);

          return (
            <div key={order.orderId} className={`ph-order-card ${expanded ? 'expanded' : ''}`}>
              <div className="ph-order-card-head" onClick={() => setExpandedId(expanded ? null : order.orderId)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setExpandedId(expanded ? null : order.orderId)}>
                <div className="ph-order-card-main">
                  <div className="ph-order-card-top">
                    <span className="ph-order-id">{order.orderId}</span>
                    <Badge tone={statusTone(order.status)}>{order.status}</Badge>
                    <Badge tone="default">{sourceLabel(order.source)}</Badge>
                    <span className="ph-order-amt">{inr(orderAmount(order))}</span>
                  </div>
                  <div className="ph-order-patient">
                    <strong>{order.patientName || '—'}</strong>
                    {order.doctorName && <span className="hint"> · {order.doctorName}</span>}
                    {(order.createdAt || '').slice(0, 10) && <span className="hint"> · {(order.createdAt || '').slice(0, 10)}</span>}
                  </div>
                  <div className="ph-order-chips">
                    {previewArr.slice(0, 3).map((name, i) => (
                      <span key={i} className="ph-med-chip">{name}</span>
                    ))}
                    {previewArr.length > 3 && <span className="ph-med-chip more">+{previewArr.length - 3} more</span>}
                    {previewArr.length === 0 && <span className="hint">No medicines on order</span>}
                  </div>
                </div>
                <div className="ph-order-card-toggle">
                  {expanded ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
                  <span>{expanded ? 'Hide' : `${lines.length || previewArr.length} med(s)`}</span>
                </div>
              </div>

              {expanded && (
                <div className="ph-order-card-body">
                  {lines.length > 0 ? (
                    <table className="data-table ph-order-inline-table">
                      <thead>
                        <tr><th>Medicine</th><th>Qty</th><th>MRP</th><th>Total</th><th>Stock link</th></tr>
                      </thead>
                      <tbody>
                        {lines.map((l, i) => {
                          const inv = inventory.find((x) => x.itemId === l.itemId);
                          return (
                            <tr key={i}>
                              <td><strong>{l.name}</strong>{l.batch && <div className="hint">Batch {l.batch}</div>}</td>
                              <td>{l.qty}</td>
                              <td>{inr(l.mrp)}</td>
                              <td style={{ fontWeight: 700 }}>{inr(lineTotal(l))}</td>
                              <td>
                                {l.itemId ? (
                                  <span style={{ color: '#16a34a', fontSize: 12 }}>✓ {inv ? `Stock ${inv.stockQty}` : 'Linked'}</span>
                                ) : (
                                  <span style={{ color: '#ca8a04', fontSize: 12 }}>⚠ Not matched</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="hint">No bill lines — open full details to match inventory from Rx.</p>
                  )}

                  {(order.rxLines || []).length > 0 && (
                    <div className="ph-order-rx-snippet">
                      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: '#64748b' }}>Rx from doctor</div>
                      {(order.rxLines || []).map((r, i) => (
                        <div key={i} style={{ fontSize: 12.5, marginBottom: 4 }}>
                          {r.drug} — {dosingLabel(r)} {r.duration ? `· ${r.duration}` : ''} · qty {r.qty || '—'}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="ph-order-card-actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => openDetail(order)}>
                      <MdVisibility style={{ verticalAlign: '-2px' }} /> Full details &amp; edit
                    </button>
                    {isPending && needsMatch && (
                      <button type="button" className="btn btn-secondary btn-sm" disabled={busy === order.orderId} onClick={() => onMatchStock(order)}>Match stock</button>
                    )}
                    {isPending && order.status !== 'dispensing' && (
                      <button type="button" className="btn btn-secondary btn-sm" disabled={busy === order.orderId} onClick={() => onSetStatus(order, 'dispensing')}>Start</button>
                    )}
                    {isPending && (
                      <button type="button" className="btn btn-primary btn-sm" disabled={busy === order.orderId} onClick={() => onSetStatus(order, 'dispensed')}>Dispense</button>
                    )}
                    {order.status === 'dispensed' && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onPrint(order)}>Bill PDF</button>
                    )}
                    {isPending && (
                      <button type="button" className="btn btn-secondary btn-sm" disabled={busy === order.orderId} onClick={() => { if (window.confirm('Cancel this order?')) onSetStatus(order, 'cancelled'); }}>Cancel</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {detailOrder && (
        <OrderDetailDrawer
          order={orders.find((o) => o.orderId === detailOrder.orderId) || detailOrder}
          inventory={inventory}
          busy={busy}
          onClose={() => setDetailOrder(null)}
          onSetStatus={onSetStatus}
          onMatchStock={onMatchStock}
          onSaveLines={onSaveLines}
          onPrint={onPrint}
        />
      )}
    </>
  );
}

/* ── New counter sale ── */
function NewSale({ inventory, onDone, onError, hospital, user, onGoToInventory }) {
  const [patientName, setPatientName] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [ipNo, setIpNo] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [lines, setLines] = useState([]);
  const [pick, setPick] = useState('');
  const [barcode, setBarcode] = useState('');
  const [busy, setBusy] = useState(false);

  const addLine = useCallback((itemId) => {
    const raw = inventory.find((i) => i.itemId === itemId);
    if (!raw) return;
    const it = suggestFefoBatch(raw, inventory) || raw;
    setLines((ls) => {
      const existing = ls.find((l) => l.itemId === it.itemId);
      if (existing) return ls.map((l) => (l.itemId === it.itemId ? { ...l, qty: l.qty + 1 } : l));
      return [...ls, {
        itemId: it.itemId, name: it.name, batch: it.batch, expiry: it.expiry,
        mrp: Number(it.mrp || 0), gstPct: Number(it.gstPct || 0), discPct: 0, qty: 1,
        stockQty: Number(it.stockQty || 0), fefo: true,
      }];
    });
    setPick('');
    setBarcode('');
  }, [inventory]);

  const scanBarcode = (code) => {
    const q = String(code || '').trim();
    if (!q) return;
    const hit = inventory.find((i) =>
      (i.barcode && i.barcode === q)
      || (i.itemId && i.itemId === q)
      || (i.name || '').toLowerCase() === q.toLowerCase()
    );
    if (hit) {
      if (Number(hit.stockQty) <= 0) { onError(`${hit.name} is out of stock`); return; }
      if (expiryState(hit.expiry) === 'expired') { onError(`${hit.name} is expired`); return; }
      addLine(hit.itemId);
    } else {
      onError(`Barcode not found: ${q}`);
    }
  };

  const updateLine = (itemId, patch) => setLines((ls) => ls.map((l) => (l.itemId === itemId ? { ...l, ...patch } : l)));
  const removeLine = (itemId) => setLines((ls) => ls.filter((l) => l.itemId !== itemId));

  const grand = lines.reduce((s, l) => {
    const gross = Number(l.qty) * Number(l.mrp);
    return s + (gross - (gross * Number(l.discPct || 0)) / 100);
  }, 0);
  const gstTotal = lines.reduce((s, l) => {
    const gross = Number(l.qty) * Number(l.mrp);
    const afterDisc = gross - (gross * Number(l.discPct || 0)) / 100;
    const gstPct = Number(l.gstPct || 0);
    return s + (gstPct > 0 ? (afterDisc * gstPct) / (100 + gstPct) : 0);
  }, 0);
  const subTotal = grand - gstTotal;

  const overStock = lines.some((l) => Number(l.qty) > Number(l.stockQty));

  const save = async () => {
    if (!lines.length) { onError('Add at least one medicine.'); return; }
    if (overStock) { onError('Reduce quantity — not enough stock for one or more medicines.'); return; }
    for (const l of lines) {
      const inv = inventory.find((i) => i.itemId === l.itemId);
      if (inv && expiryState(inv.expiry) === 'expired') {
        onError(`Cannot sell expired medicine: ${l.name}`);
        return;
      }
    }
    setBusy(true);
    try {
      const isCredit = paymentMode === 'Credit (Due)';
      const res = await ipApi.createPharmacyOrder({
        patientName: patientName || 'Counter sale',
        doctorName,
        admissionId: ipNo || undefined,
        lines: lines.map(({ stockQty, ...l }) => ({ ...l, hsn: inventory.find((i) => i.itemId === l.itemId)?.hsn || l.hsn })),
        paymentMode,
        status: isCredit ? 'pending' : 'dispensed',
        source: 'counter',
        hospitalId: user?.hospitalId,
      });
      if (!isCredit && res?.order) printPharmacyBill(res.order, hospital, user);
      setLines([]); setPatientName(''); setDoctorName(''); setIpNo('');
      onDone(isCredit ? 'Credit sale saved as pending — collect payment then dispense from Orders.' : 'Sale completed. Bill printed.');
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!inventory.length) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>💊</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>No Medicines in Inventory</div>
        <div style={{ fontSize: 14, color: '#64748b', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
          You must add medicines to the inventory first before making a sale.
          <br /><br />
          Go to the <strong>💊 Inventory tab</strong> → Click <strong>"Add Medicine"</strong> → Fill in name, batch, expiry, MRP, stock quantity → Save.
          <br /><br />
          Then come back here to make a sale.
        </div>
        <button type="button" className="btn btn-primary" style={{ fontSize: 15, padding: '12px 32px' }} onClick={onGoToInventory}>
          💊 Go to Inventory → Add Medicine
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <h3>🛒 New Counter Sale / Dispensing</h3>
      <div className="form-grid mb">
        <div className="field"><label>Patient / Customer Name</label><input className="input" value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder="Walk-in customer / Patient name" /></div>
        <div className="field">
          <label>Doctor (if prescribed)</label>
          <input className="input" value={doctorName} onChange={(e) => setDoctorName(e.target.value)} placeholder="Dr. Name (optional)" />
        </div>
        <div className="field">
          <label>Payment Mode</label>
          <select className="input" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
            {['Cash', 'UPI / GPay', 'Debit Card', 'Credit Card', 'Insurance / TPA', 'Credit (Due)'].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="field">
          <label>IPD No. (if admitted)</label>
          <input className="input" value={ipNo} onChange={(e) => setIpNo(e.target.value)} placeholder="IP-534/2025 (optional)" />
        </div>
        <div className="field full">
          <label>📷 Scan barcode (USB scanner / type + Enter)</label>
          <input
            className="input"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                scanBarcode(barcode);
              }
            }}
            placeholder="Scan medicine barcode — adds to bill instantly"
            autoComplete="off"
          />
        </div>
        <div className="field full">
          <label>🔍 Search &amp; Add Medicine</label>
          <select className="input" value={pick} onChange={(e) => addLine(e.target.value)}>
            <option value="">— Select medicine from inventory ({inventory.length} available) —</option>
            {inventory.map((i) => (
              <option key={i.itemId} value={i.itemId} disabled={Number(i.stockQty) <= 0 || expiryState(i.expiry) === 'expired'}>
                {i.name}{i.genericName ? ` (${i.genericName})` : ''}{i.form ? ` · ${i.form}` : ''}{i.batch ? ` · Batch: ${i.batch}` : ''} · MRP: {inr(i.mrp)} · Stock: {i.stockQty} {i.unit || 'units'}{Number(i.stockQty) <= 0 ? ' [OUT OF STOCK]' : ''}{expiryState(i.expiry) === 'expired' ? ' [EXPIRED]' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {lines.length > 0 && (
        <div className="table-wrap mb">
          <table className="data-table">
            <thead>
              <tr>
                <th>Medicine</th><th>Qty</th><th>MRP</th><th>Disc %</th><th>GST %</th><th>Amount</th><th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.itemId}>
                  <td>
                    {l.name}
                    {Number(l.qty) > Number(l.stockQty) && <div className="hint" style={{ color: '#dc2626' }}>Only {l.stockQty} in stock</div>}
                  </td>
                  <td><input className="input input-sm" type="number" min={1} style={{ width: 70 }} value={l.qty} onChange={(e) => updateLine(l.itemId, { qty: Number(e.target.value) })} /></td>
                  <td><input className="input input-sm" type="number" min={0} style={{ width: 90 }} value={l.mrp} onChange={(e) => updateLine(l.itemId, { mrp: Number(e.target.value) })} /></td>
                  <td><input className="input input-sm" type="number" min={0} max={100} style={{ width: 70 }} value={l.discPct} onChange={(e) => updateLine(l.itemId, { discPct: Number(e.target.value) })} /></td>
                  <td><input className="input input-sm" type="number" min={0} style={{ width: 70 }} value={l.gstPct} onChange={(e) => updateLine(l.itemId, { gstPct: Number(e.target.value) })} /></td>
                  <td>{inr(lineTotal(l))}</td>
                  <td><button type="button" className="btn btn-sm btn-secondary" onClick={() => removeLine(l.itemId)} aria-label="Remove"><MdDelete /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pharmacy-sale-foot">
        <div className="sale-totals">
          <div><span className="hint">Subtotal</span><strong>{inr(subTotal)}</strong></div>
          <div><span className="hint">GST</span><strong>{inr(gstTotal)}</strong></div>
          <div className="sale-grand"><span>Total</span><strong>{inr(grand)}</strong></div>
        </div>
        <button type="button" className="btn btn-primary" disabled={busy || !lines.length || overStock} onClick={save}>
          {busy ? 'Saving…' : 'Save & print bill'}
        </button>
      </div>
      {overStock && <p className="hint" style={{ color: '#dc2626', marginTop: 8 }}>Reduce quantity — sale blocked until stock is sufficient.</p>}
    </div>
  );
}

/* ── Inventory master ── */
function InventoryTab({ inventory, loading, onChanged, onError, autoOpenAdd, onAutoOpenDone }) {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_MED);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stockModal, setStockModal] = useState(null);
  const [stockAddQty, setStockAddQty] = useState('10');
  const [stockBusy, setStockBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  React.useEffect(() => {
    if (autoOpenAdd) {
      setForm(EMPTY_MED);
      setEditId(null);
      setShowForm(true);
      onAutoOpenDone?.();
    }
  }, [autoOpenAdd, onAutoOpenDone]);

  const filtered = inventory.filter((i) => {
    if (!showInactive && i.active === false) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (i.name || '').toLowerCase().includes(q)
      || (i.brandName || '').toLowerCase().includes(q)
      || (i.genericName || '').toLowerCase().includes(q)
      || (i.saltComposition || '').toLowerCase().includes(q)
      || (i.therapeuticClass || '').toLowerCase().includes(q)
      || (i.barcode || '').toLowerCase().includes(q);
  });
  const inactiveCount = inventory.filter((i) => i.active === false).length;

  const startAdd = () => { setForm(EMPTY_MED); setEditId(null); setShowForm(true); };
  const startEdit = (it) => {
    setForm({
      name: it.name || '', brandName: it.brandName || '', genericName: it.genericName || '',
      saltComposition: it.saltComposition || '', therapeuticClass: it.therapeuticClass || it.category || '',
      manufacturer: it.manufacturer || '', form: it.form || 'Tablet', unit: it.unit || 'nos',
      packSize: it.packSize || '', barcode: it.barcode || '', batch: it.batch || '', expiry: it.expiry || '',
      mrp: it.mrp ?? '', purchasePrice: it.purchasePrice ?? '', gstPct: it.gstPct ?? 12,
      stockQty: it.stockQty ?? '', reorderLevel: it.reorderLevel ?? 10, hsn: it.hsn || '', rackNo: it.rackNo || '',
      imageUrl: it.imageUrl || '', isOtc: !!it.isOtc,
    });
    setEditId(it.itemId);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { onError('Medicine name is required.'); return; }
    setBusy(true);
    try {
      if (editId) {
        // Never overwrite live stockQty from a stale edit form — use Add stock for qty
        const { stockQty, ...master } = form;
        await ipApi.updateInventoryItem(editId, master);
      } else {
        await ipApi.createInventoryItem(form);
      }
      setShowForm(false); setForm(EMPTY_MED); setEditId(null);
      onChanged(editId ? 'Medicine updated.' : 'Medicine added to inventory.');
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Medicine',
      render: (r) => (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {r.imageUrl ? (
            <img src={r.imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
          ) : null}
          <div>
            <strong style={r.active === false ? { color: '#94a3b8', textDecoration: 'line-through' } : undefined}>{r.name}</strong>
            {r.isOtc && <Badge tone="info">OTC</Badge>}
            {r.active === false && <Badge tone="danger">Inactive</Badge>}
            {(r.saltComposition || r.genericName) && <div className="hint">{r.saltComposition || r.genericName}</div>}
            {r.therapeuticClass && <div className="hint" style={{ color: '#6366f1' }}>{r.therapeuticClass}</div>}
          </div>
        </div>
      ),
    },
    { key: 'packSize', label: 'Pack', render: (r) => r.packSize || r.unit || '—' },
    { key: 'batch', label: 'Batch', render: (r) => r.batch || '—' },
    {
      key: 'expiry',
      label: 'Expiry',
      render: (r) => {
        const st = expiryState(r.expiry);
        if (!r.expiry) return '—';
        const tone = st === 'expired' ? 'danger' : st === 'soon' ? 'warning' : 'default';
        return <Badge tone={tone}>{r.expiry}</Badge>;
      },
    },
    {
      key: 'stockQty',
      label: 'Stock',
      render: (r) => {
        const low = Number(r.stockQty) <= Number(r.reorderLevel || 0);
        return <span style={low ? { color: '#dc2626', fontWeight: 700 } : undefined}>{r.stockQty} {r.unit}</span>;
      },
    },
    { key: 'mrp', label: 'MRP', render: (r) => inr(r.mrp) },
    { key: 'gstPct', label: 'GST', render: (r) => `${r.gstPct || 0}%` },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => startEdit(r)}>Edit</button>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setStockModal(r); setStockAddQty('10'); }}>+ Stock</button>
          {r.active !== false ? (
            <button type="button" className="btn btn-sm btn-secondary" onClick={async () => {
              if (!window.confirm(`Deactivate ${r.name}? It will be hidden from sale dropdowns.`)) return;
              try { await ipApi.updateInventoryItem(r.itemId, { active: false }); onChanged('Medicine deactivated.'); }
              catch (err) { onError(err.message); }
            }}>Deactivate</button>
          ) : (
            <button type="button" className="btn btn-sm btn-secondary" onClick={async () => {
              try { await ipApi.updateInventoryItem(r.itemId, { active: true }); onChanged('Medicine reactivated.'); }
              catch (err) { onError(err.message); }
            }}>Activate</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Prominent banner when inventory is empty */}
      {!loading && inventory.length === 0 && !showForm && (
        <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)', borderRadius: 16, padding: 28, marginBottom: 20, color: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>💊</div>
          <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 6 }}>Inventory is Empty</div>
          <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 20 }}>Add medicines to inventory before making any sale or dispensing from EMR prescriptions.</div>
          <button type="button" className="btn" style={{ background: '#fff', color: '#1d4ed8', fontWeight: 900, fontSize: 15, padding: '12px 32px', borderRadius: 10, border: 'none', cursor: 'pointer' }} onClick={startAdd}>
            <MdAdd style={{ verticalAlign: '-3px', marginRight: 6 }} /> Add First Medicine to Inventory
          </button>
        </div>
      )}
      <div className="card mb inventory-toolbar">
        <input className="input" placeholder="🔍 Search medicine name, generic name, barcode…" value={search} onChange={(e) => setSearch(e.target.value)} />
        {inactiveCount > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, whiteSpace: 'nowrap', cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show inactive ({inactiveCount})
          </label>
        )}
        <button type="button" className="btn btn-primary" onClick={startAdd} style={{ whiteSpace: 'nowrap' }}><MdAdd /> Add Medicine</button>
      </div>

      {showForm && (
        <form className="form-grid card mb" onSubmit={save}>
          <h3 className="full" style={{ margin: 0 }}>{editId ? 'Edit medicine' : 'Add medicine'}</h3>
          <div className="field full"><label>Product name *</label><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Paracetamol Tablets IP 650 mg" /></div>
          <div className="field"><label>Brand name</label><input className="input" value={form.brandName} onChange={(e) => setForm({ ...form, brandName: e.target.value })} placeholder="e.g. Dolo-650" /></div>
          <div className="field"><label>Manufacturer</label><input className="input" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} placeholder="e.g. Micro Labs" /></div>
          <div className="field full"><label>Salt composition</label><input className="input" value={form.saltComposition} onChange={(e) => setForm({ ...form, saltComposition: e.target.value, genericName: e.target.value })} placeholder="e.g. Paracetamol 650 mg" /></div>
          <div className="field"><label>Therapeutic class</label><input className="input" value={form.therapeuticClass} onChange={(e) => setForm({ ...form, therapeuticClass: e.target.value })} placeholder="e.g. Analgesic / Antipyretic" /></div>
          <div className="field"><label>Product form</label>
            <select className="input" value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })}>
              {['Tablet', 'Capsule', 'Syrup', 'Injection', 'Ointment', 'Drops', 'Cream', 'Suspension', 'Inhaler', 'Other'].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="field"><label>Pack size</label><input className="input" value={form.packSize} onChange={(e) => setForm({ ...form, packSize: e.target.value })} placeholder="e.g. 10 x 15 Tablets" /></div>
          <div className="field"><label>Unit</label>
            <select className="input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
              {['nos', 'strip', 'bottle', 'vial', 'tube', 'ml', 'gm'].map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="field"><label>MRP ₹ (latest)</label><input className="input" type="number" min={0} step="0.01" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} /></div>
          <div className="field"><label>Purchase price ₹</label><input className="input" type="number" min={0} step="0.01" value={form.purchasePrice} onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })} /></div>
          <div className="field"><label>GST %</label><input className="input" type="number" min={0} value={form.gstPct} onChange={(e) => setForm({ ...form, gstPct: e.target.value })} /></div>
          <div className="field"><label>Product image URL</label><input className="input" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} placeholder="https://… pack photo" /></div>
          <div className="field"><label>Barcode</label><input className="input" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="EAN / internal code" /></div>
          <div className="field"><label>Batch no.</label><input className="input" value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })} /></div>
          <div className="field"><label>Expiry (month)</label><input className="input" type="month" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} /></div>
          <div className="field"><label>Stock qty</label><input className="input" type="number" min={0} value={form.stockQty} onChange={(e) => setForm({ ...form, stockQty: e.target.value })} /></div>
          <div className="field"><label>Reorder level</label><input className="input" type="number" min={0} value={form.reorderLevel} onChange={(e) => setForm({ ...form, reorderLevel: e.target.value })} /></div>
          <div className="field"><label>HSN</label><input className="input" value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} /></div>
          <div className="field"><label>Rack / shelf</label><input className="input" value={form.rackNo} onChange={(e) => setForm({ ...form, rackNo: e.target.value })} /></div>
          <div className="field" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={!!form.isOtc} onChange={(e) => setForm({ ...form, isOtc: e.target.checked })} />
              OTC product (no Rx required)
            </label>
          </div>
          {form.imageUrl ? (
            <div className="field full">
              <label>Image preview</label>
              <img src={form.imageUrl} alt="" style={{ maxHeight: 80, borderRadius: 8, border: '1px solid #e2e8f0' }} onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          ) : null}
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : editId ? 'Save changes' : 'Add to inventory'}</button>
          </div>
        </form>
      )}

      {stockModal && (
        <div className="emr-modal-backdrop" onClick={() => setStockModal(null)} role="presentation">
          <div className="emr-modal card" onClick={(e) => e.stopPropagation()}>
            <div className="emr-modal-header">
              <div>
                <h3 style={{ margin: 0 }}>Add stock — {stockModal.name}</h3>
                <div className="hint">Current: {stockModal.stockQty} {stockModal.unit || 'units'}</div>
              </div>
              <button type="button" className="rcp-close" onClick={() => setStockModal(null)}><MdClose /></button>
            </div>
            <div className="emr-modal-body">
              <div className="field">
                <label>Quantity to add</label>
                <input className="input" type="number" min={1} value={stockAddQty} onChange={(e) => setStockAddQty(e.target.value)} />
              </div>
              <div className="form-actions" style={{ marginTop: 14 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setStockModal(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={stockBusy} onClick={async () => {
                  const add = Number(stockAddQty);
                  if (!add || add <= 0) return;
                  setStockBusy(true);
                  try {
                    await ipApi.updateInventoryItem(stockModal.itemId, { addStock: add });
                    onChanged(`Added ${add} to ${stockModal.name}.`);
                    setStockModal(null);
                  } catch (err) { onError(err.message); }
                  finally { setStockBusy(false); }
                }}>{stockBusy ? 'Saving…' : 'Add stock'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DataTable columns={columns} rows={filtered} keyField="itemId" loading={loading} emptyMessage={search ? 'No medicines match your search.' : 'No medicines yet — click “Add medicine” to build your inventory.'} />
    </>
  );
}

/* ── Purchase Orders Tab (API) ── */
const PO_STATUSES = ['Draft', 'Ordered', 'Partially Received', 'Received', 'Cancelled'];

function PurchaseOrdersTab({ onError, onStockChange }) {
  const { data, loading, reload } = useLiveData(() => ipApi.getPharmacyPurchaseOrders(80), []);
  const { data: supData } = useLiveData(() => ipApi.getPharmacySuppliers(100), []);
  const orders = data?.orders || [];
  const suppliers = supData?.suppliers || [];
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ supplierId: '', supplier: '', date: new Date().toISOString().slice(0, 10), expectedDelivery: '', remarks: '', items: [{ name: '', qty: 1, unit: 'strips', rate: 0 }] });
  const [expandedPo, setExpandedPo] = useState(null);

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { name: '', qty: 1, unit: 'strips', rate: 0 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));

  const submitPO = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const sup = suppliers.find((s) => s.name === form.supplier || s.supplierId === form.supplierId);
      await ipApi.createPharmacyPurchaseOrder({
        ...form,
        supplierId: sup?.supplierId || form.supplierId || '',
        supplier: form.supplier || sup?.name || '',
      });
      setShowForm(false);
      setForm({ supplierId: '', supplier: '', date: new Date().toISOString().slice(0, 10), expectedDelivery: '', remarks: '', items: [{ name: '', qty: 1, unit: 'strips', rate: 0 }] });
      await reload();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (poId, status) => {
    setBusy(poId);
    try {
      await ipApi.patchPharmacyPurchaseOrder(poId, { status });
      if (status === 'Received') await onStockChange?.();
      await reload();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy('');
    }
  };

  const STATUS_COLOR = { Draft: '#94a3b8', Ordered: '#3b82f6', 'Partially Received': '#f59e0b', Received: '#10b981', Cancelled: '#ef4444' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Purchase Orders</span>
          <span className="hint" style={{ marginLeft: 8 }}>{orders.filter((o) => o.status === 'Ordered').length} pending · saved to cloud</span>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>+ New PO</button>
      </div>

      {showForm && (
        <form className="card mb" onSubmit={submitPO}>
          <h3 style={{ marginTop: 0 }}>New Purchase Order</h3>
          <div className="form-grid">
            <div className="field"><label>Supplier *</label>
              <select className="input" required value={form.supplierId || ''} onChange={(e) => {
                const sup = suppliers.find((s) => s.supplierId === e.target.value);
                setForm({ ...form, supplierId: e.target.value, supplier: sup?.name || '' });
              }}>
                <option value="">— Select supplier —</option>
                {suppliers.map((s) => <option key={s.supplierId} value={s.supplierId}>{s.name}</option>)}
              </select>
              {!suppliers.length && (
                <input className="input mt" required value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Or type supplier name" />
              )}
            </div>
            <div className="field"><label>Order date</label><input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div className="field"><label>Expected delivery</label><input type="date" className="input" value={form.expectedDelivery} onChange={(e) => setForm({ ...form, expectedDelivery: e.target.value })} /></div>
            <div className="field"><label>Remarks</label><input className="input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Order Items</div>
            {form.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input className="input" style={{ flex: 3, minWidth: 150 }} placeholder="Medicine name" required value={item.name} onChange={(e) => updateItem(i, { name: e.target.value })} />
                <input type="number" className="input" style={{ width: 80 }} placeholder="Qty" value={item.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} />
                <select className="input" style={{ width: 100 }} value={item.unit} onChange={(e) => updateItem(i, { unit: e.target.value })}>
                  {['strips', 'bottles', 'vials', 'pcs', 'boxes'].map((u) => <option key={u}>{u}</option>)}
                </select>
                <input type="number" className="input" style={{ width: 100 }} placeholder="Rate ₹" value={item.rate} onChange={(e) => updateItem(i, { rate: e.target.value })} />
                {form.items.length > 1 && <button type="button" onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>×</button>}
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addItem}>+ Add Item</button>
          </div>
          <div className="form-actions" style={{ marginTop: 14 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Create Purchase Order'}</button>
          </div>
        </form>
      )}

      {loading && <p className="hint">Loading purchase orders…</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {orders.map((po) => (
          <div key={po.poId} className="card" style={{ borderLeft: `4px solid ${STATUS_COLOR[po.status] || '#e2e8f0'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{po.supplier}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{po.poId} · Ordered: {po.date}{po.expectedDelivery ? ` · Expected: ${po.expectedDelivery}` : ''}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#0d6e6a' }}>₹{Number(po.totalAmount || 0).toLocaleString('en-IN')}</div>
                <span style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: (STATUS_COLOR[po.status] || '#94a3b8') + '20', color: STATUS_COLOR[po.status] }}>{po.status}</span>
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => setExpandedPo(expandedPo === po.poId ? null : po.poId)}>
              {expandedPo === po.poId ? '▲ Hide items' : `▼ ${(po.items || []).length} item(s)`}
            </button>
            {expandedPo === po.poId && (
              <div style={{ overflowX: 'auto', marginTop: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead><tr style={{ background: '#f8fafc' }}>{['Item', 'Qty', 'Unit', 'Rate', 'Total'].map((h) => <th key={h} style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #e2e8f0' }}>{h}</th>)}</tr></thead>
                  <tbody>{(po.items || []).map((it, i) => <tr key={i}><td style={{ padding: '6px 10px', border: '1px solid #f1f5f9' }}>{it.name}</td><td>{it.qty}</td><td>{it.unit}</td><td>₹{it.rate}</td><td>₹{(Number(it.qty) * Number(it.rate)).toLocaleString('en-IN')}</td></tr>)}</tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {PO_STATUSES.map((s) => (
                <button key={s} type="button" className="ins-status-btn" disabled={busy === po.poId} style={{ background: po.status === s ? STATUS_COLOR[s] : undefined, color: po.status === s ? '#fff' : undefined }} onClick={() => updateStatus(po.poId, s)}>{s}</button>
              ))}
            </div>
            {po.status === 'Received' && <p className="hint" style={{ marginTop: 6, fontSize: 11 }}>Stock auto-added to inventory for matching medicine names.</p>}
          </div>
        ))}
        {!loading && orders.length === 0 && <p className="hint table-empty">No purchase orders yet.</p>}
      </div>
    </div>
  );
}

/* ── Suppliers Tab (API) ── */
function SuppliersTab({ onError }) {
  const { data, loading, reload } = useLiveData(() => ipApi.getPharmacySuppliers(100), []);
  const suppliers = data?.suppliers || [];
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', contact: '', phone: '', email: '', address: '', gst: '', category: 'Medicines', paymentTerms: 'Net 30', rating: 4 });
  const EMPTY_SUP = { name: '', contact: '', phone: '', email: '', address: '', gst: '', category: 'Medicines', paymentTerms: 'Net 30', rating: 4 };

  const startEdit = (s) => {
    setForm({
      name: s.name || '', contact: s.contact || '', phone: s.phone || '', email: s.email || '',
      address: s.address || '', gst: s.gst || '', category: s.category || 'Medicines',
      paymentTerms: s.paymentTerms || 'Net 30', rating: s.rating ?? 4,
    });
    setEditId(s.supplierId);
    setShowForm(true);
  };

  const saveSupplier = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await ipApi.patchPharmacySupplier(editId, form);
      else await ipApi.createPharmacySupplier(form);
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_SUP);
      await reload();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Supplier Directory ({suppliers.length}) · cloud saved</span>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>+ Add Supplier</button>
      </div>
      {showForm && (
        <form className="card mb form-grid" onSubmit={saveSupplier}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>{editId ? 'Edit supplier' : 'Add supplier'}</h3>
          <div className="field"><label>Company name *</label><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="field"><label>Contact person</label><input className="input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
          <div className="field"><label>Phone</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="field"><label>Email</label><input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="field"><label>Address / City</label><input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="field"><label>GST No.</label><input className="input" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
          <div className="field"><label>Category</label>
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {['Medicines', 'Surgical Supplies', 'Lab Reagents', 'Equipment', 'Consumables', 'Other'].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : editId ? 'Save changes' : 'Add supplier'}</button>
          </div>
        </form>
      )}
      {loading && <p className="hint">Loading suppliers…</p>}
      <div className="bb-donors-grid">
        {suppliers.map((s) => (
          <div key={s.supplierId} className="bb-donor-card" style={{ flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#0d6e6a', color: '#fff', fontWeight: 800, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(s.name || '?').charAt(0)}</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13.5 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{s.category} · {s.paymentTerms}</div>
                </div>
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => startEdit(s)}><MdEdit /></button>
            </div>
            <div style={{ fontSize: 12, color: '#475569' }}>
              {s.phone && <div>📞 {s.phone}{s.contact ? ` (${s.contact})` : ''}</div>}
              {s.email && <div>✉ {s.email}</div>}
              {s.gst && <div>🏷 GST: {s.gst}</div>}
            </div>
          </div>
        ))}
        {!loading && suppliers.length === 0 && <p className="hint">No suppliers yet — add your first distributor.</p>}
      </div>
    </div>
  );
}

/* ── Pharmacy Reports Tab ── */
function PharmacyReportsTab({ orders, inventory }) {
  const dispensed = orders.filter((o) => normStatus(o.status) === 'dispensed');
  const totalRevenue = dispensed.reduce((s, o) => s + Number(o.amount || o.total || 0), 0);
  const stockValue = inventory.reduce((s, i) => s + Number(i.stockQty || 0) * Number(i.purchasePrice || i.mrp || 0), 0);
  const saleValue = inventory.reduce((s, i) => s + Number(i.stockQty || 0) * Number(i.mrp || 0), 0);

  const topMeds = [...inventory].sort((a, b) => Number(b.stockQty || 0) - Number(a.stockQty || 0)).slice(0, 8);

  return (
    <div>
      <div className="stat-grid" style={{ marginBottom: 16 }}>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#10b981' }}>₹{totalRevenue.toLocaleString('en-IN')}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Total Revenue (dispensed)</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#8b5cf6' }}>₹{stockValue.toLocaleString('en-IN')}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Stock Purchase Value</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#0d6e6a' }}>₹{saleValue.toLocaleString('en-IN')}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Stock MRP Value</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#f59e0b' }}>{dispensed.length}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Orders Dispensed</div>
        </div>
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Top Medicines by Stock Quantity</h3>
        {topMeds.map((med, i) => {
          const maxQty = Math.max(...topMeds.map((m) => Number(m.stockQty || 0)));
          const pct = maxQty > 0 ? (Number(med.stockQty || 0) / maxQty) * 100 : 0;
          return (
            <div key={med.itemId || i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ width: 28, height: 28, background: '#f0fdf4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#16a34a', flexShrink: 0 }}>{i + 1}</span>
              <span style={{ width: 180, fontSize: 12.5, fontWeight: 600, flexShrink: 0 }}>{med.name}</span>
              <div style={{ flex: 1, height: 10, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#0d6e6a', borderRadius: 999, transition: 'width 0.5s' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0d6e6a', width: 60, textAlign: 'right' }}>{med.stockQty} {med.unit || 'units'}</span>
            </div>
          );
        })}
        {topMeds.length === 0 && <p className="hint">No inventory data.</p>}
      </div>
    </div>
  );
}

/* ═══ Drug Returns Tab (API) ═══ */
function DrugReturnsTab({ inventory, onError, onStockChange }) {
  const RETURN_REASONS = ['Excess dispensed', 'Doctor changed prescription', 'Patient discharged early', 'Wrong drug dispensed', 'Patient refused medication', 'Adverse drug reaction', 'Other'];
  const { data, loading, reload } = useLiveData(() => ipApi.getPharmacyReturns(80), []);
  const returns = data?.returns || [];
  const [form, setForm] = React.useState({ patient: '', ipId: '', drug: '', itemId: '', qty: 1, mrp: '', reason: 'Excess dispensed', returnType: 'OPD' });
  const [showForm, setShowForm] = React.useState(false);
  const [busy, setBusy] = React.useState('');
  const [expandedId, setExpandedId] = React.useState(null);

  const pickDrug = (itemId) => {
    const hit = (inventory || []).find((i) => i.itemId === itemId);
    if (!hit) return;
    setForm((f) => ({ ...f, drug: hit.name, itemId: hit.itemId, mrp: hit.mrp || '' }));
  };

  async function submitReturn(e) {
    e.preventDefault();
    setBusy('new');
    try {
      await ipApi.createPharmacyReturn({
        patientName: form.patient,
        admissionId: form.ipId,
        drug: form.drug,
        itemId: form.itemId,
        qty: Number(form.qty),
        mrp: Number(form.mrp),
        reason: form.reason,
        returnType: form.returnType,
      });
      setForm({ patient: '', ipId: '', drug: '', itemId: '', qty: 1, mrp: '', reason: 'Excess dispensed', returnType: 'OPD' });
      setShowForm(false);
      await reload();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function updateStatus(returnId, status) {
    setBusy(returnId);
    try {
      await ipApi.patchPharmacyReturn(returnId, { status });
      if (status === 'Approved') await onStockChange?.();
      await reload();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy('');
    }
  }

  const totalRefunds = returns.filter((r) => r.status === 'Approved').reduce((s, r) => s + Number(r.refundAmt || 0), 0);
  const pending = returns.filter((r) => r.status === 'Pending').length;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Returns', val: returns.length, color: '#6366f1' }, { label: 'Pending Approval', val: pending, color: '#f59e0b' }, { label: 'Total Refunded', val: inr(totalRefunds), color: '#10b981' }].map((s) => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 14, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 14 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>{showForm ? '✕ Cancel' : '+ New Drug Return'}</button></div>
      {showForm && (
        <form className="card mb" style={{ borderLeft: '4px solid #f97316' }} onSubmit={submitReturn}>
          <h3 style={{ marginTop: 0, color: '#f97316' }}>↩ Drug Return / Refund</h3>
          <div className="form-grid mb">
            <div className="field"><label>Return Type</label><select className="input" value={form.returnType} onChange={(e) => setForm((f) => ({ ...f, returnType: e.target.value }))}><option>OPD</option><option>IPD</option></select></div>
            <div className="field"><label>Patient Name *</label><input className="input" required value={form.patient} onChange={(e) => setForm((f) => ({ ...f, patient: e.target.value }))} /></div>
            <div className="field"><label>OP/IP ID</label><input className="input" value={form.ipId} onChange={(e) => setForm((f) => ({ ...f, ipId: e.target.value }))} /></div>
            <div className="field"><label>Drug from inventory</label>
              <select className="input" value={form.itemId} onChange={(e) => pickDrug(e.target.value)}>
                <option value="">— Type manually below —</option>
                {(inventory || []).map((i) => <option key={i.itemId} value={i.itemId}>{i.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Drug Name *</label><input className="input" required value={form.drug} onChange={(e) => setForm((f) => ({ ...f, drug: e.target.value }))} /></div>
            <div className="field"><label>Qty Returned</label><input type="number" min={1} className="input" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} /></div>
            <div className="field"><label>MRP per unit (₹)</label><input type="number" className="input" value={form.mrp} onChange={(e) => setForm((f) => ({ ...f, mrp: e.target.value }))} /></div>
            <div className="field full"><label>Return Reason *</label><select className="input" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}>{RETURN_REASONS.map((r) => <option key={r}>{r}</option>)}</select></div>
          </div>
          <div className="form-actions"><button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button><button type="submit" className="btn btn-primary" disabled={busy === 'new'}>{busy === 'new' ? 'Saving…' : 'Submit Return'}</button></div>
        </form>
      )}
      {loading && <p className="hint">Loading returns…</p>}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Return Register · cloud saved</h3>
        <div className="ph-order-list">
          {returns.map((r) => {
            const expanded = expandedId === r.returnId;
            return (
              <div key={r.returnId} className={`ph-order-card ${expanded ? 'expanded' : ''}`}>
                <div className="ph-order-card-head" onClick={() => setExpandedId(expanded ? null : r.returnId)} role="button" tabIndex={0}>
                  <div className="ph-order-card-main">
                    <div className="ph-order-card-top">
                      <span className="ph-order-id">{r.returnId}</span>
                      <Badge tone={r.status === 'Approved' ? 'success' : r.status === 'Rejected' ? 'danger' : 'warning'}>{r.status}</Badge>
                      <span className="ph-order-amt">{inr(r.refundAmt)}</span>
                    </div>
                    <div className="ph-order-patient"><strong>{r.patientName}</strong> · {r.drug} × {r.qty}</div>
                    <div className="hint">{(r.createdAt || '').slice(0, 10)} · {r.reason}</div>
                  </div>
                  <div className="ph-order-card-toggle">{expanded ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}</div>
                </div>
                {expanded && (
                  <div className="ph-order-card-body">
                    <dl className="rcp-kv">
                      <dt>Return type</dt><dd>{r.returnType || 'OPD'}</dd>
                      <dt>Admission / OP ID</dt><dd>{r.admissionId || '—'}</dd>
                      <dt>MRP per unit</dt><dd>{inr(r.mrp)}</dd>
                      <dt>Refund amount</dt><dd style={{ fontWeight: 800, color: '#10b981' }}>{inr(r.refundAmt)}</dd>
                      <dt>Inventory link</dt><dd>{r.itemId || 'Manual entry (no stock restore)'}</dd>
                    </dl>
                    {r.status === 'Pending' && (
                      <div className="ph-order-card-actions">
                        <button type="button" className="btn btn-primary btn-sm" disabled={busy === r.returnId} onClick={() => updateStatus(r.returnId, 'Approved')}>Approve &amp; restore stock</button>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy === r.returnId} onClick={() => updateStatus(r.returnId, 'Rejected')}>Reject</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!loading && returns.length === 0 && <p className="hint table-empty">No returns recorded yet.</p>}
      </div>
    </div>
  );
}

/* ═══ Pharmacy Analytics Tab ═══ */
function PharmacyAnalyticsTab({ inventory, orders }) {
  const dispensed = (orders || []).filter((o) => normStatus(o.status) === 'dispensed');
  const salesByDrug = {};
  dispensed.forEach((o) => {
    (o.lines || []).forEach((l) => {
      const key = l.name || 'Unknown';
      if (!salesByDrug[key]) salesByDrug[key] = { drug: key, qtySold: 0, revenue: 0 };
      salesByDrug[key].qtySold += Number(l.qty || 0);
      salesByDrug[key].revenue += lineTotal(l);
    });
  });
  const salesData = Object.values(salesByDrug).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  const totalRevenue = dispensed.reduce((s, o) => s + Number(o.amount || o.total || 0), 0);
  const totalQty = salesData.reduce((s, d) => s + d.qtySold, 0);
  const expiryAlerts = (inventory || [])
    .filter((i) => expiryState(i.expiry) === 'soon' || expiryState(i.expiry) === 'expired')
    .map((i) => {
      const [y, m] = String(i.expiry).split('-').map(Number);
      const days = Math.floor((new Date(y, m, 0) - Date.now()) / 86400000);
      return { drug: i.name, batch: i.batch || '—', expiry: i.expiry, qty: i.stockQty, days };
    })
    .sort((a, b) => a.days - b.days)
    .slice(0, 8);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1 }}>Pharmacy Analytics (live)</h3>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Revenue', val: inr(totalRevenue), color: '#10b981' }, { label: 'Items Sold', val: totalQty.toLocaleString(), color: '#6366f1' }, { label: 'Dispensed Orders', val: dispensed.length, color: '#f59e0b' }, { label: 'Stock Items', val: (inventory || []).length, color: '#3b82f6' }].map((s) => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 14, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Top selling drugs (from dispensed orders)</h3>
          {salesData.length === 0 && <p className="hint">No dispensed sales yet.</p>}
          {salesData.map((d, i) => (
            <div key={d.drug} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
              <div><span style={{ color: '#94a3b8', fontWeight: 700, marginRight: 8 }}>{i + 1}.</span><span style={{ fontWeight: 600, fontSize: 13 }}>{d.drug}</span></div>
              <div style={{ fontWeight: 700, color: '#10b981', fontSize: 13 }}>{inr(d.revenue)}</div>
            </div>
          ))}
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0, color: '#dc2626' }}>Expiry alerts (from inventory)</h3>
          {expiryAlerts.length === 0 && <p className="hint">No medicines expiring in the next 90 days.</p>}
          {expiryAlerts.map((item) => (
            <div key={`${item.drug}-${item.batch}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
              <div><div style={{ fontWeight: 700, fontSize: 13 }}>{item.drug}</div><div style={{ fontSize: 11, color: '#64748b' }}>Batch: {item.batch} · Qty: {item.qty}</div></div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800, color: item.days < 30 ? '#dc2626' : item.days < 60 ? '#f59e0b' : '#64748b' }}>{item.days < 0 ? 'Expired' : `${item.days}d`}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{item.expiry}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
